import { useState } from 'react';
import type { ApiClientErrorCode, DealerChannel, ShopContact } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { buildMaxOpenLink } from '@/lib/messengerLinks';
import { copyShareTextToClipboard } from '@/lib/clipboardCopy';
import { openExternal } from '@/lib/openExternal';
import { apiClient } from '@/lib/apiClient';
import { pingShopContact } from '@/lib/shopContactPing';
import { CITIES, CITY_TO_LANGUAGE, shopMessage, type ShopMessageKind } from './shopMessage';

/**
 * Presenter Экрана 2.4.4 «Найти в магазинах» (см. ADR-0005, ADR-0007).
 * 5 состояний (P-021): idle / loading / success / empty / error.
 *
 * Кнопки мессенджеров (WA/TG/MAX) расположены вертикально (P-019 — горизонтальные
 * ряды интерактивных элементов запрещены, на 5"-экранах с пыльными руками
 * пользователи промахиваются).
 *
 * ── ADR-0083 Фаза 2: серверный резолв контакта (анти-парсинг P-031) ──────────
 * Кнопки строятся из `contacts.counts` (счётчики каналов) + `shop.id` (dealerId) +
 * индекса — НЕ из массивов номеров. Клиент номеров БОЛЬШЕ НЕ ЧИТАЕТ и wa.me/t.me-
 * ссылки сам НЕ строит: при клике делается АВТОРИЗОВАННЫЙ `apiClient.dealers.contact
 * (dealerId, channel, i)` (header-bearer JWT), эндпоинт резолвит и отдаёт JSON, а
 * клиент сам зовёт `openExternal`. Почему JSON, а не 302: браузер-навигация
 * (`openExternal`) заголовок авторизации НЕ несёт → 302-эндпоинт дал бы 401
 * (ADR-0083 Amendment). Номер/URL приходят ТОЛЬКО при клике под авторизацией — в
 * статичном DOM списка их нет.
 *
 * Два типа кнопок (ВСЕ — `<button>`, НИКАКИХ `<a href>`; P-031):
 *  - `open` — WhatsApp / «Позвонить» (`tel:`): клик → резолв `{ url }` → `openExternal`.
 *  - `action` — двухшаговый flow для Telegram/MAX (ни тот, ни другой не дают
 *    чат-по-номеру с префиллом): тап → копируем СООБЩЕНИЕ в буфер (синхронно) →
 *    резолв (`{ url }` для TG, `{ number }` для MAX) → персистентный центр-оверлей
 *    с инструкцией и ЯВНОЙ кнопкой открытия. Мессенджер открывается ТОЛЬКО по этой
 *    кнопке (иначе встаёт поверх страницы раньше, чем пользователь прочитает
 *    инструкцию). Для MAX по кнопке открытия — клиент-пинг `shop_contact(channel='max')`
 *    (бэк по MAX событие не эмитит — резолв отдаёт только номер).
 *
 * P-031 анти-парсинг: НИ ОДНОГО номера в статичном DOM — ни в тексте кнопки, ни в
 * `aria-label`, ни в `href`. Несколько кнопок одного канала различаются ИНДЕКСОМ
 * («(2)»/«(3)»). Номер живёт только в ответе резолва (при клике) и показывается в
 * оверлее MAX (для ручного поиска).
 *
 * Канальные кнопки (Telegram-username из `telegram[]`, MAX-username из `maxId[]`)
 * НЕ рендерятся — этот экран только для ПРЯМОЙ связи с менеджерами по номеру.
 *
 * Per ADR-0007: Presenter принимает уже готовый `messageKind: ShopMessageKind`
 * (entity-context маппинг сделан в Container — HandlesShopsRoute / DecorsShopsRoute).
 */
export type ShopsListState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface ShopsListViewProps {
  state: ShopsListState;
  shops?: readonly ShopContact[];
  error?: { code: ApiClientErrorCode; message: string };
  // Контекст товара — для генерации сообщения
  productName: string;
  productGlobalId: string;
  /**
   * Обязателен только для `messageKind='paint'` (RAL-код в шаблоне).
   * Для остальных kinds игнорируется.
   * См. ADR-0007 «Контракт shopMessage» и shopMessage.ts paint case.
   */
  ralCode?: string;
  messageKind: ShopMessageKind;
  /** Бренд каталога (verbatim) — слот «<изделие> <название>, <бренд>» (Фаза 6). paint — не задаётся (RAL). */
  productBrand?: string;
  /** Подкатегория декора (verbatim) — для спец-ярлыка «Пленка для фасадов» (МДФ
   *  Пленка, решение Aibek). decor only; handles/paint — undefined. */
  productSubcategory?: string;
  /** Чистый URL карточки товара (без query) — добавляется в конец запроса дилеру (Фаза 6.1, item 4). */
  productUrl?: string;
  // Город (UI selector)
  selectedCity: string;
  onCityChange: (city: string) => void;
  /**
   * Опции пикера города. Список из `dealers.cities()` (distinct из dealers,
   * канон-порядок) — для ВСЕХ kind'ов (decor/furniture/handles/paint, канон-20,
   * решение Aibek 2026-06-15). Статичный `CITIES` остаётся лишь как
   * defensive-fallback (`props.cityOptions ?? CITIES`), Container всегда передаёт
   * динамический список.
   */
  cityOptions?: readonly string[];
  /** Текст empty-состояния. Decor: «Дилеров этого бренда…» (ADR-0060 §3). */
  emptyText?: string;
  // Callbacks
  onBackToCard?: () => void;
  onRetry?: () => void;
}

const DEFAULT_EMPTY_TEXT = 'В этом городе магазины не найдены. Выберите другой город.';

/** Текст транзиентной ошибки резолва (ADR-0083 Фаза 2): не ломает экран (P-021). */
const RESOLVE_ERROR_TEXT = 'Не удалось получить контакт. Проверьте интернет и попробуйте снова.';

/** Инструкция оверлея Telegram (двухшаговый flow). */
const TG_INSTRUCTION =
  'Сообщение скопировано. Откройте чат и вставьте: зажмите поле ввода → Вставить';

/**
 * Контекст оверлея-инструкции для двухшагового flow (Telegram/MAX). Цель открытия
 * (`openUrl`) уже РЕЗОЛВНУТА на шаге 1 (TG → `{ url }`; MAX → `buildMaxOpenLink()`),
 * поэтому в DOM оверлея чужого URL нет — для MAX показывается только номер в
 * `instruction` (ручной поиск). `channel`/`dealerId` — для клиент-пинга MAX.
 */
interface CopyPrompt {
  instruction: string;
  openLabel: string;
  openUrl: string;
  channel: Extract<DealerChannel, 'telegram' | 'max'>;
  dealerId: string;
}

/** Запрос на резолв контакта (клик по канальной кнопке карточки). */
interface ContactActivation {
  dealerId: string;
  channel: DealerChannel;
  index: number;
  /** `open` — WA/«Позвонить» (открыть сразу); `action` — TG/MAX (двухшаговый оверлей). */
  variant: 'open' | 'action';
  /** Сообщение запроса (prefill для WhatsApp + буфер для TG/MAX). */
  message: string;
  /** Уникальный id кнопки (`<dealerId>:<channel>-<i>`) — для индикатора загрузки. */
  buttonId: string;
}

export function ShopsListView(props: ShopsListViewProps) {
  const cityOptions = props.cityOptions ?? CITIES;
  const emptyText = props.emptyText ?? DEFAULT_EMPTY_TEXT;
  const filtered = props.shops?.filter((s) => s.city === props.selectedCity) ?? [];
  const effectiveState: ShopsListState =
    props.state === 'success' && filtered.length === 0 ? 'empty' : props.state;

  // Оверлей-инструкция для двухшагового flow. Персистентный центр-оверлей: висит,
  // пока пользователь не закроет (крестик/тап вне). Один на весь экран.
  const [copyPrompt, setCopyPrompt] = useState<CopyPrompt | null>(null);
  // Кнопка, по которой идёт резолв (loading-индикатор, P-021). Глобально-уникальна
  // (`<dealerId>:<channel>-<i>`), чтобы при нескольких карточках подсветить ровно ту.
  const [pendingId, setPendingId] = useState<string | null>(null);
  // Транзиентная ошибка резолва — НЕ переводит экран в error-состояние (карточки
  // остаются), показывается строкой role="alert" над списком (ADR-0083 Фаза 2).
  const [resolveError, setResolveError] = useState<string | null>(null);
  const dismissPrompt = () => setCopyPrompt(null);

  /**
   * Клик по канальной кнопке. `open` (WA/«Позвонить») → резолв `{ url }` → открыть
   * сразу. `action` (TG/MAX) → СНАЧАЛА синхронно копируем сообщение в буфер
   * (`copyShareTextToClipboard` инициирует `writeText` до первого await — требование
   * браузера: copy внутри user-gesture), затем резолвим и показываем оверлей.
   * Ошибка резолва не ломает экран — показываем транзиентную строку.
   */
  const handleContact = async (a: ContactActivation) => {
    setResolveError(null);
    if (a.variant === 'action') {
      // Шаг 1 (синхронно, до await): копируем СООБЩЕНИЕ запроса в буфер.
      void copyShareTextToClipboard(a.message);
    }
    setPendingId(a.buttonId);
    try {
      // Авторизованный резолв (header-bearer JWT). text (prefill) — только WhatsApp.
      const res = await apiClient.dealers.contact(
        a.dealerId,
        a.channel,
        a.index,
        a.channel === 'whatsapp' ? a.message : undefined,
      );
      // Сужение union по каналу: WA/TG/call → { url }, MAX → { number }. Контракт
      // это гарантирует; если форма неожиданная (дрейф) — не молчим, а показываем
      // транзиентную ошибку (а не «ничего не произошло» после копирования в буфер).
      if (a.variant === 'open' && 'url' in res) {
        // WA/«Позвонить»: открыть резолвнутый URL (wa.me / tel:) сразу.
        openExternal(res.url);
      } else if (a.channel === 'telegram' && 'url' in res) {
        // TG: показать оверлей с резолвнутым t.me/+<номер> (открыть — по кнопке оверлея).
        setCopyPrompt({
          instruction: TG_INSTRUCTION,
          openLabel: 'Открыть Telegram',
          openUrl: res.url,
          channel: 'telegram',
          dealerId: a.dealerId,
        });
      } else if (a.channel === 'max' && 'number' in res) {
        // MAX: чата-по-номеру нет; показать номер в оверлее (ручной поиск), открыть
        // САМО приложение (buildMaxOpenLink, без номера) по кнопке оверлея.
        setCopyPrompt({
          instruction: `Найдите магазин в MAX по номеру ${res.number}, сообщение в буфере — вставьте`,
          openLabel: 'Открыть MAX',
          openUrl: buildMaxOpenLink(),
          channel: 'max',
          dealerId: a.dealerId,
        });
      } else {
        // Неожиданная форма ответа для канала — не оставляем пользователя без сигнала.
        setResolveError(RESOLVE_ERROR_TEXT);
      }
    } catch {
      // Резолв упал (сеть/401/404) — не ломаем экран, показываем подсказку.
      setResolveError(RESOLVE_ERROR_TEXT);
    } finally {
      setPendingId(null);
    }
  };

  // Шаг 2 двухшагового flow: явная кнопка открытия в оверлее. `openExternal` сам
  // выбирает механизм (app-схема `max://` на Android → прямая навигация; http(s) →
  // новая вкладка). Оверлей оставляем открытым (по возвращении инструкция видна).
  // Для MAX — клиент-пинг shop_contact (бэк по MAX событие не эмитит).
  const handleOpenFromPrompt = () => {
    if (!copyPrompt) return;
    openExternal(copyPrompt.openUrl);
    if (copyPrompt.channel === 'max') {
      pingShopContact({ dealerId: copyPrompt.dealerId, channel: 'max' });
    }
  };

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Найти в магазинах" />

      <section className="p-6 flex flex-col gap-4">
        <label className="flex flex-col gap-2 text-base text-ink">
          <span className="font-semibold">Город</span>
          <select
            aria-label="Город"
            value={props.selectedCity}
            onChange={(e) => props.onCityChange(e.target.value)}
            className="min-h-tap rounded-lg bg-card border-[1.5px] border-accent px-4 text-ink text-base"
          >
            {cityOptions.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="flex-1 px-6 pb-6 overflow-y-auto">
        {effectiveState === 'idle' && <p className="text-muted text-base">Загрузка магазинов…</p>}
        {effectiveState === 'loading' && <p className="text-muted text-base">Загружаем…</p>}
        {effectiveState === 'empty' && (
          <p className="text-muted text-base text-center max-w-[280px] mx-auto mt-8">{emptyText}</p>
        )}
        {effectiveState === 'success' && (
          <div className="flex flex-col gap-4">
            {resolveError && (
              // Транзиентная ошибка резолва — экран не ломается (P-021), карточки на месте.
              <p role="alert" className="text-error text-base m-0">
                {resolveError}
              </p>
            )}
            {filtered.map((shop) => (
              <ShopCard
                key={shop.id}
                shop={shop}
                productName={props.productName}
                productBrand={props.productBrand}
                productSubcategory={props.productSubcategory}
                productUrl={props.productUrl}
                productGlobalId={props.productGlobalId}
                ralCode={props.ralCode}
                messageKind={props.messageKind}
                pendingId={pendingId}
                onContact={handleContact}
              />
            ))}
          </div>
        )}
        {effectiveState === 'error' && <ErrorState error={props.error} onRetry={props.onRetry} />}
      </section>

      {copyPrompt && (
        // Двухшаговый flow: центр-оверлей с инструкцией + явная кнопка открытия.
        // Закрытие — крестиком или тапом по затемнённому фону (backdrop).
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <button
            type="button"
            aria-label="Закрыть подсказку"
            onClick={dismissPrompt}
            className="absolute inset-0 bg-ink/40 cursor-default"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Как отправить сообщение"
            className="relative z-10 w-full max-w-[320px] rounded-xl bg-card text-ink shadow-card p-5 pt-10 flex flex-col gap-4"
          >
            <button
              type="button"
              aria-label="Закрыть"
              onClick={dismissPrompt}
              className="absolute top-2 right-2 min-h-tap min-w-[44px] text-ink text-2xl leading-none"
            >
              <span aria-hidden="true">✕</span>
            </button>
            <p role="status" aria-live="polite" className="text-ink text-base m-0">
              {copyPrompt.instruction}
            </p>
            <button type="button" onClick={handleOpenFromPrompt} className={BUTTON_CLASS}>
              {copyPrompt.openLabel}
            </button>
          </div>
        </div>
      )}

      <BottomBar />
    </main>
  );
}

interface ShopCardProps {
  shop: ShopContact;
  productName: string;
  productBrand?: string;
  productSubcategory?: string;
  productUrl?: string;
  productGlobalId: string;
  /** Обязателен для `messageKind='paint'`, см. ADR-0007 / shopMessage.ts. */
  ralCode?: string;
  messageKind: ShopMessageKind;
  /** Id кнопки, по которой идёт резолв (loading-индикатор, P-021). */
  pendingId: string | null;
  /** Клик по канальной кнопке → резолв контакта (ADR-0083 Фаза 2). */
  onContact: (activation: ContactActivation) => void;
}

/**
 * Дескриптор канальной кнопки (ADR-0083 Фаза 2). БЕЗ номера и БЕЗ URL — только
 * канал + индекс + подписи. Реальный резолв (`apiClient.dealers.contact`) идёт ПРИ
 * КЛИКЕ в `ShopsListView.handleContact` по `(dealerId, channel, index)`.
 *  - `open` — WhatsApp / «Позвонить»: открыть резолвнутый URL сразу.
 *  - `action` — Telegram / MAX: двухшаговый оверлей (буфер → инструкция → открытие).
 *
 * P-031 анти-парсинг: НИ ОДНОГО номера в статичном DOM. Кнопки одного канала
 * различаются ИНДЕКСОМ («(2)», «(3)»), не номером.
 */
interface ChannelButton {
  id: string;
  channel: DealerChannel;
  index: number;
  variant: 'open' | 'action';
  text: string;
  aria: string;
}

/** Подпись канала с индекс-дизамбигуацией (1-й — без суффикса, далее «(N)»). */
const channelText = (base: string, index: number): string =>
  index === 0 ? base : `${base} (${index + 1})`;
/** aria канала: подпись + «магазину <name>» (БЕЗ номера, P-031). */
const channelAria = (base: string, index: number, name: string): string =>
  `${channelText(base, index)} магазину ${name}`;

/** Метаданные канала: базовая подпись + тип кнопки. Порядок групп — WA→TG→MAX→call. */
const CHANNEL_META: ReadonlyArray<{
  channel: DealerChannel;
  base: string;
  variant: 'open' | 'action';
  countKey: keyof ShopContact['contacts']['counts'];
}> = [
  { channel: 'whatsapp', base: 'Написать в WhatsApp', variant: 'open', countKey: 'whatsapp' },
  { channel: 'telegram', base: 'Написать в Telegram', variant: 'action', countKey: 'telegram' },
  { channel: 'max', base: 'Написать в MAX', variant: 'action', countKey: 'max' },
  { channel: 'call', base: 'Позвонить', variant: 'open', countKey: 'call' },
];

/**
 * Кнопки прямой связи, сгруппированные ПО КАНАЛУ: все WhatsApp → все Telegram →
 * все MAX → все «Позвонить» (решение Aibek 2026-06-09). Количество в каждом канале
 * = `contacts.counts` (ADR-0083 Фаза 3 — wire несёт ТОЛЬКО счётчики с бэка, массивов
 * номеров в контракте больше нет; клиент номеров не видит, P-031).
 *
 * P-031: текст/aria БЕЗ номера; резолв при клике по (dealerId, channel, index).
 */
function buildChannelButtons(shop: ShopContact): ChannelButton[] {
  const counts = shop.contacts.counts;
  const out: ChannelButton[] = [];
  for (const meta of CHANNEL_META) {
    const n = counts[meta.countKey];
    for (let i = 0; i < n; i++) {
      out.push({
        id: `${meta.channel}-${i}`,
        channel: meta.channel,
        index: i,
        variant: meta.variant,
        text: channelText(meta.base, i),
        aria: channelAria(meta.base, i, shop.name),
      });
    }
  }
  return out;
}

const BUTTON_CLASS = [
  'block w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
  'font-semibold text-base text-center py-3 px-2',
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
  'disabled:opacity-60 no-underline',
].join(' ');

function ShopCard({
  shop,
  productName,
  productBrand,
  productSubcategory,
  productUrl,
  productGlobalId,
  ralCode,
  messageKind,
  pendingId,
  onContact,
}: ShopCardProps) {
  const language = CITY_TO_LANGUAGE[shop.city] ?? 'ru';
  const message = shopMessage({
    kind: messageKind,
    productName,
    brand: productBrand,
    subcategory: productSubcategory,
    productUrl,
    globalId: productGlobalId,
    language,
    ralCode,
  });

  // Все кнопки контактов, сгруппированы по каналу (без лимита/disclosure —
  // список скроллится внутри, P-046; ни один канал не прячется).
  const buttons = buildChannelButtons(shop);

  // Анти-парсинг (P-031): ВСЕ кнопки — `<button>` (никаких `<a href>`). Резолв при
  // клике (`onContact` → apiClient.dealers.contact). В статичном DOM номера нет.
  const renderButton = (b: ChannelButton) => {
    const buttonId = `${shop.id}:${b.id}`;
    return (
      <button
        key={b.id}
        type="button"
        onClick={() =>
          onContact({
            dealerId: shop.id,
            channel: b.channel,
            index: b.index,
            variant: b.variant,
            message,
            buttonId,
          })
        }
        aria-label={b.aria}
        aria-busy={pendingId === buttonId}
        disabled={pendingId === buttonId}
        className={BUTTON_CLASS}
      >
        {b.text}
      </button>
    );
  };

  return (
    <article className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-5">
      <h3 className="font-display text-h2 text-ink m-0">{shop.name}</h3>
      {shop.address && <p className="text-muted text-base mt-1 mb-4">{shop.address}</p>}
      {/* Вертикальный список кнопок прямой связи (P-019), сгруппирован по каналу:
          все WhatsApp → все Telegram → все MAX → все «Позвонить». Без лимита —
          скролл живёт на внешней секции списка (P-046). */}
      <div className="flex flex-col gap-3">
        {buttons.map(renderButton)}
        {buttons.length === 0 && (
          <p className="text-muted text-sm">У магазина пока нет контактов.</p>
        )}
      </div>
    </article>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error?: { code: ApiClientErrorCode; message: string };
  onRetry?: () => void;
}) {
  const isUnauthorized = error?.code === 'UNAUTHORIZED';
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      {isUnauthorized ? (
        <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
          Зарегистрируйтесь, чтобы увидеть контакты магазинов.
        </p>
      ) : (
        <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
          Не удалось получить ответ. Проверьте интернет и попробуйте снова.
        </p>
      )}
      {onRetry && !isUnauthorized && (
        <button
          type="button"
          onClick={onRetry}
          aria-label="Повторить запрос"
          className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
        >
          Повторить
        </button>
      )}
    </div>
  );
}
